import { CloseCircleOutlined, CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Input, message, Space, Table, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "../../server";
import { getProductVidName, productVids } from "../../extensions/helper";
import "./sale-add.css";
import SaleAddItem from "./sale-add-item";
import { ftmPrice } from "../../extensions/utils";
import { dictsGetUsers, dictsDictionary2Options } from "../../extensions/dicts";
import { isMobile } from "../../extensions/design";
const { Text } = Typography;

const SaleAdd = ({ onClose, clientId, visitId, activeBtns }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVid, setSelectedVid] = useState(0);
  const [search, setSearch] = useState();
  const [sales, setSales] = useState([]);
  const onCancel = () => onClose();
  const [executorUsersMap, setExecutorUsersMap] = useState({});
  const [showCart, setShowCart] = useState(false);
  // Состояние блокировки "Сохранить" из-за позиций, у которых исполнитель не может оказать услугу/услуги пакета
  const [saveBlocked, setSaveBlocked] = useState(false);
  const [blockedItems, setBlockedItems] = useState(new Map());
  const [confirmedItems, setConfirmedItems] = useState(new Map());
  // Мобильный баннер-предупреждение свёрнут по умолчанию
  const [mobileWarningCollapsed, setMobileWarningCollapsed] = useState(true);

  const getData = () => {
    setLoading(true);
    serverFetch("product")
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(() => {
    getData();
  }, [])

  const columns = [
    { dataIndex: "name", title: "Наименование", width: 250, },
    { dataIndex: "expUnit", title: "Ед.", width: 80, },
    { dataIndex: "expPrice", title: "Цена", align: "right", width: 80 },
    { dataIndex: "vid", title: "Вид", render: value => <div>{getProductVidName(value)}</div> },
    { dataIndex: "storeQty", title: "Ост.", align: "right", width: 80 },
  ]

  const allButtons = [...productVids, { value: -1, label: "Все" }];
  const allowedLabels = activeBtns || allButtons.map(b => b.label);
  const firstEnabledValue = allButtons.find(b => allowedLabels.includes(b.label))?.value ?? 0;

  const toolButtons = allButtons.map(i => (
    <Button key={i.value} 
      className={selectedVid === i.value ? "sport-btn-selected" : ""}
      disabled={!allowedLabels.includes(i.label)} 
      onClick={() => setSelectedVid(i.value)} >{i.label}</Button> 
  ));

  useEffect(() => {
    const currentLabel = allButtons.find(b => b.value === selectedVid)?.label;
    if (!allowedLabels.includes(currentLabel)) {
      setSelectedVid(firstEnabledValue);
    }
  }, [selectedVid, activeBtns]);

  const filteredItems = items.filter(i => {
    if (selectedVid !== -1 && selectedVid !== i.vid)
      return false;
    if (search && i.name.toUpperCase().indexOf(search.toUpperCase()) === -1)
      return false;
    return i.active;
  });

  const onAddInSales = record => {
    setSales([...sales, 
      { productName: record.name, qty: 1, clientId, visitId, productId: record.id, price: record.expPrice, deleted: false, status: 0,
        syma: record.expPrice, expUnit: record.expUnit, discountPrc: 0, storeQty: record.storeQty, vid: record.vid }])
  }

  // Выполнить сохранение продажи (нормализация + вызов sale/List)
  const performSave = () => {
    const normalizedSales = sales.map((s) => {
      const price = Number.parseFloat(s.price);
      const qty = Number.parseFloat(s.qty);
      const discountPrc = Number.parseFloat(s.discountPrc);
      return {
        ...s,
        price: Number.isFinite(price) ? price : 0,
        qty: Number.isFinite(qty) ? qty : 1,
        discountPrc: Number.isFinite(discountPrc) ? discountPrc : 0,
      };
    });
    serverFetch("sale/List", { method: "POST", bodyData: normalizedSales})
      .then(() => onClose(true))
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const onSave = () => {
    // Проверить исполнители для всех позиций, где задан исполнитель и ещё не все продукты подтверждены
    const asks = sales.map((s, idx) => ({
      idx: idx,
      productId: s.productId,
      userId: s.userId ?? null,
    })).filter(a => {
      if (a.userId == null) return false;
      const missing = blockedItems.get(a.idx) ?? [];
      const confirmed = confirmedItems.get(a.idx) ?? [];
      // Если позиции ещё не было в блокировке - валидируем в первый раз. Иначе валидируем только если есть неподтверждённые продукты
      if (!blockedItems.has(a.idx)) return true;
      return missing.some(m => !confirmed.includes(m));
    });

    if (asks.length === 0) {
      // Все позиции с исполнителями уже подтверждены или исполнители не заданы - сохраняем
      performSave();
      return;
    }

    setLoading(true);
    serverFetch("sale/CheckPerformers", { method: "POST", bodyData: asks })
      .then(results => {
        setLoading(false);
        const newBlocked = new Map();
        results.forEach(r => {
          if (!r.ok) {
            newBlocked.set(r.idx, r.missingProductIds ?? []);
          }
        });
        if (newBlocked.size > 0) {
          setBlockedItems(newBlocked);
          setConfirmedItems(new Map());
          setSaveBlocked(true);
          return;
        }
        // Все позиции прошли проверку - выполняем сохранение продажи
        performSave();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  const onSaleChange = (newItem, idx) => {
    const newSales = [...sales];
    newSales[idx] = newItem;
    setSales(newSales);
    // При смене исполнителя позиция требует повторной проверки при следующем сохранении
    setBlockedItems(prev => { const next = new Map(prev); next.delete(idx); return next; });
    setConfirmedItems(prev => { const next = new Map(prev); next.delete(idx); return next; });
    setSaveBlocked(false);
  }

  const isInCart = item =>
    sales.some(s => s.productId === item.id);
  
  const toggleInSales = record => {
    setSales(prev => {
      const exists = prev.some(s => s.productId === record.id);
  
      if (exists) {
        return prev.filter(s => s.productId !== record.id);
      }
  
      return [...prev,
        { productName: record.name, qty: 1, clientId, visitId, productId: record.id, price: record.expPrice, deleted: false,
          status: 0, syma: record.expPrice, expUnit: record.expUnit, discountPrc: 0, storeQty: record.storeQty, vid: record.vid}
      ];
    });
  };
  
  const onSaleDelete = idx => {
    setSales([...sales.slice(0, idx), ...sales.slice(idx + 1)]);
    // Удаляем позицию из состояния блокировки
    setBlockedItems(prev => {
      const next = new Map(prev);
      next.delete(idx);
      if (next.size === 0) setSaveBlocked(false);
      return next;
    });
    setConfirmedItems(prev => { const next = new Map(prev); next.delete(idx); return next; });
  };

  // Очистить корзину и сбросить состояние блокировки "Сохранить"
  const onClearCart = () => {
    setSales([]);
    setBlockedItems(new Map());
    setConfirmedItems(new Map());
    setSaveBlocked(false);
  };

  // Подтвердить позицию корзины (пользователь нажал "Да" в всплывающем окне) - добавить исполнителя к услугам пакета
  const onConfirmItem = idx => {
    const missingProductIds = blockedItems.get(idx) ?? [];
    if (missingProductIds.length === 0) return;

    // Добавляем исполнителя к каждому продукту, для которого он не является исполнителем
    // Запросы выполняются последовательно через цепочку .then() (без Promise.all)
    setLoading(true);

    const chain = missingProductIds.reduce(
      (prev, productId) => prev.then(() =>
        serverFetch("user/ProductAdd", { method: "POST", bodyData: { UserId: sales[idx]?.userId, ProductId: productId } })
      ),
      Promise.resolve()
    );

    chain
      .then(() => {
        setLoading(false);
        message.success("Исполнитель добавлен к услугам пакета");
        setConfirmedItems(prev => {
          const next = new Map(prev);
          next.set(idx, missingProductIds);
          return next;
        });
        setBlockedItems(prev => {
          const next = new Map(prev);
          next.delete(idx);
          if (next.size === 0) setSaveBlocked(false);
          return next;
        });
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const loadExecutorUsers = (productId, vid) => {
    if ((vid === 1 || vid === 2 || vid === 3) && productId) {
      serverFetch("User/ProductUsersAsUsers/" + productId)
        .then(data => {
          if (data && data.length > 0) {
            setExecutorUsersMap(prev => ({
              ...prev,
              [productId]: dictsDictionary2Options(data)
            }));
          } else {
            dictsGetUsers(users => {
              setExecutorUsersMap(prev => ({
                ...prev,
                [productId]: users
              }));
            }, { activeOnly: true });
          }
        })
        .catch(e => message.error(e.userMessage));
    } else {
      dictsGetUsers(users => {
        setExecutorUsersMap(prev => ({
          ...prev,
          [productId]: users
        }));
      }, { activeOnly: true });
    }
  };

  useEffect(() => {
    sales.forEach(item => {
      if (!executorUsersMap[item.productId]) {
        loadExecutorUsers(item.productId, item.vid);
      }
    });
  }, [sales]);


  // Баннер-предупреждение, показываемый когда "Сохранить" заблокировано из-за неразрешённых позиций
  const divBlockedBanner = saveBlocked && blockedItems.size > 0 ? (
    <Alert message="Некоторые позиции требуют внимания" description="Исполнитель не может оказать услугу или все услуги пакета. Нажмите на иконку предупреждения у позиции, чтобы подтвердить исполнителя или сменить его." type="warning" showIcon />
  ) : null;

  return isMobile? (
    <div className="sport-modal">
      <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onCancel(false)}>
        <div className="sport-modal-content" onMouseDown={e => e.stopPropagation()}>

          {!showCart && (
            <>
              <div className="sport-modal-title">
                <span>Продажа</span>
                <CloseOutlined className="sport-section-title-close" onClick={onCancel} />
              </div>

              <div className="sport-section" style={{ marginBottom: 12 }}>
              <Space>
                  <Select className="client-abon-form-item" value={selectedVid} onChange={val => setSelectedVid(val)}
                    options={allButtons
                      .filter(b => allowedLabels.includes(b.label))
                      .map(b => ({ value: b.value, label: b.label }))}
                  />
                  <Input className="client-abon-form-item" placeholder="Поиск..." value={search} onChange={e => setSearch(e.target.value)} allowClear/>
                </Space>
              </div>

              <div className="sales-items-list" style={{ overflowY: "scroll", overflowX: "clip"}}>
                {filteredItems.map(item => {
                  const inCart = isInCart(item);

                  return (
                    <div key={item.id} className={`client-item-sale ${inCart ? "in-cart" : ""}`} onClick={() => toggleInSales(item)}>
                      <div className="sale-item-body">
                        <div className="client-item-line-bold-double">
                          <div className="client-item-clip">{item.name}</div>
                          <div className="client-item-clip-right">{item.expPrice}</div>
                        </div>

                        <div className="client-item-abon-card-sum-sale-num-fields">
                          <Text className="client-item-clip">{getProductVidName(item.vid)}</Text>
                          <Text className="client-item-clip">{item.expUnit}</Text>
                          <Text className="client-item-clip">
                          Ост.: {item.storeQty}
                          </Text>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between" }}>
                <Button onClick={onClearCart} disabled={sales.length === 0}>Очистить</Button>
                <Button type="primary" onClick={() => setShowCart(true)} disabled={sales.length === 0}>
                  В корзину ({sales.length})
                </Button>
              </div>
            </>
          )}

          {showCart && (<div className="sport-modal">
      <div className="sport-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose(false)}>
        <div className="sport-modal-content" onMouseDown={(e) => e.stopPropagation()}>
          <div className="sport-modal-title">
            <span>Корзина</span>
            <CloseOutlined className="sport-section-title-close" onClick={() => setShowCart(false)} style={{ cursor: "pointer", fontSize: "16px", marginLeft: "auto" }}/>
          </div>
          {sales.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center" }}>Корзина пуста</div>
          ) : (
            <div style={{ maxHeight: "65vh", overflowY: "auto", overflowX: "hidden" }}>
              {saveBlocked && blockedItems.size > 0 && (
                <div style={{ border: "1px solid #ffa940", backgroundColor: "#fff7cc", padding: 6, marginBottom: 8, borderRadius: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, marginBottom: mobileWarningCollapsed ? 0 : 4 }} onClick={() => setMobileWarningCollapsed(!mobileWarningCollapsed)}>
                    <ExclamationCircleOutlined style={{ color: "#ffa940" }} />
                    <span>Некоторые позиции требуют внимания</span>
                  </div>
                  {!mobileWarningCollapsed && (
                    <div style={{ fontSize: 12, lineHeight: 1.3, color: "#333" }}>
                      Исполнитель не может оказать услугу или все услуги пакета. Нажмите на иконку предупреждения у позиции, чтобы подтвердить исполнителя или сменить его.
                    </div>
                  )}
                </div>
              )}
              {sales.map((item, idx) => (
                <SaleAddItem key={idx} item={item} idx={idx} onChange={onSaleChange} onDelete={onSaleDelete} executorUsers={executorUsersMap[item.productId] || []} blocked={blockedItems.has(idx)} confirmed={confirmedItems.has(idx)} onConfirm={() => onConfirmItem(idx)}/>
              ))}
            </div>
          )}
          <div className="client-item-line-double-halfed">
            <Button onClick={onClearCart}>Очистить</Button>
            <Button disabled={sales.length <= 0 || loading || saveBlocked} onClick={onSave}>Сохранить</Button>
          </div>
        </div>
      </div>
    </div>)}
    </div>
    </div>
    </div>
  ) : (<div className="sport-modal">
    <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose(false)}>
      <div className="sport-modal-content" style={{width: 800}} onMouseDown={e => e.stopPropagation()}>
        <div className="sport-modal-title"><span>Продажа</span><CloseCircleOutlined className="sport-section-title-close" onClick={onCancel}/></div>
        <div className="sport-section">
          <div className="sport-section-row"><b>Выбор товара/услуги</b></div>
          <Space><Input placeholder="Поиск..." value={search} onChange={e => setSearch(e.target.value)} />{toolButtons}</Space>
          <Table loading={loading} dataSource={filteredItems} columns={columns} size="small" pagination={false} 
            onRow={(record) => ({onClick: () => onAddInSales(record)})} scroll={{y: 400}} rowKey="id"/>
        </div>
        {sales.length ? <div className="sport-section">
          <div className="sport-section-row"><b>Корзина</b></div>
          <SaleAddItem />
          {sales.map((item, idx) => <SaleAddItem item={item} idx={idx} key={idx} onChange={onSaleChange} onDelete={onSaleDelete} executorUsers={executorUsersMap[item.productId] || []} blocked={blockedItems.has(idx)} confirmed={confirmedItems.has(idx)} onConfirm={() => onConfirmItem(idx, item.productId, item.userId)} /> )}
          <div className="sale-add-item-line sale-add-item-header">
            <div />
            <div style={{ gridColumn: "2 / 8", justifyContent: "flex-end" }}>ИТОГО</div>
            <div className="ta-right">{ftmPrice(sales.reduce((prev, i) => prev + i.syma, 0))}</div>
          </div>
          {divBlockedBanner}
          <div className="sale-add-buttons">
            <Button onClick={onClearCart}>Очистить</Button>
            <Button disabled={sales.length <= 0 || loading || saveBlocked} onClick={onSave}>Сохранить</Button>
            <Button onClick={() => onClose(false)}>Закрыть</Button>
          </div>
        </div> : null}
      </div>
    </div>
  </div>);
}

export default SaleAdd;